import Sidebar from '~/modules/identification/components/Sidebar/Sidebar.tsx';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useGetIdentificationSteps } from './hooks/useGetIdentificationSteps.ts';
import { useGetStepsComponents } from './hooks/useGetStepsComponents.tsx';
import { getCompleatedFormByStepName, getNextStep } from './utils.ts';
import {
  IdentificationFormSteps,
  StepStatus,
  TIdentificationStep,
  TInfoModal,
  TitleIdentificationBanner,
} from './types.ts';
import useGetContragentTypes from './hooks/useGetContragentTypes.ts';
import { Box, Paper, Theme, Typography, useMediaQuery, useTheme } from '@mui/material';
import type { SystemStyleObject } from '@mui/system';
import { useGetSidebarSize } from './hooks/useGetSidebarSize.ts';
import { styles } from './styles.ts';
import { IdentificationType } from './forms/GettingStarted/types.ts';
import {
  setIdentificationError,
  setIsReidentification,
  updateFormData,
} from '~/store/identification-form/slices.ts';
import { useAppDispatch } from '~/store/appDispatch.ts';
import { useSetSearchParam } from '~/hooks/useSetSearchParam.ts';
import { InfoModal } from '~/components/modals/InfoModal.tsx';
import { useNavigate } from 'react-router';
import { IdnetificationFormContext } from './contexts/IdentificationFormContext.ts';
import {
  selectCompletedForms,
  selectGettingStarted,
  selectIdentificationError,
  selectIsReidentification,
} from '~/store/identification-form/selectors.ts';
import { EMPTY_ERROR } from '~/constants/errorCodes.ts';
import ModalAlert from '~/components/modals/ModalAlert/ModalAlert.tsx';
import { useGetIsStepDisabled } from './hooks/useGetIsStepDisabled.ts';
import store from '~/store/store.ts';
import { gettingStartedStep } from './constants.ts';

const MainForm = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const { searchParams, setSearchParams, removeParam } = useSetSearchParam();

  const { isLegalResident, isSoleTrader } = useGetContragentTypes();
  const { sidebarWidth, isOpened, setIsOpened, mainContainerMargin } = useGetSidebarSize();

  const completedForms = useSelector(selectCompletedForms);
  const { identificationNumber, applicationId } = useSelector(selectGettingStarted) || {};
  const { errorMessage, discard } = useSelector(selectIdentificationError) || {};
  const isReidentification = useSelector(selectIsReidentification);
  const isStepDisabledByName = useGetIsStepDisabled();

  const [activeStep, setActiveStep] = useState<TIdentificationStep>(gettingStartedStep);
  const { steps, setSteps } = useGetIdentificationSteps(activeStep.name);
  const [isFormDirty, setIsFormDirty] = useState<boolean>(false);
  const pendingStepRef = useRef<TIdentificationStep | (() => void) | null>(null);

  const handleOnErrorAlertClose = () => {
    discard?.();
    dispatch(setIdentificationError(EMPTY_ERROR));
  };

  const defaultModalInfo: TInfoModal = useMemo(
    () => ({
      title: 'Вітаємо!',
      message: '',
      primaryButtonTitle: 'Вийти',
      secondaryButtonTitle: 'Повернутись на головну',
      onClose: () => navigate('/services'),
      isOpened: false,
      onPrimaryButtonClick: () => navigate('/services'),
      onSecondaryClick: () => {
        navigate('/services');
        setInfoModal(defaultModalInfo);
      },
    }),
    []
  );

  const [infoModal, setInfoModal] = useState<TInfoModal>(defaultModalInfo);
  const [targetForm, setTargetForm] = useState<IdentificationFormSteps | null>(null);

  const type = searchParams.get('type');
  const identificationText = isReidentification
    ? TitleIdentificationBanner.SecondaryIdentification
    : TitleIdentificationBanner.FirstIdentification;
  const applicationLabel =
    completedForms?.isGettingStartedCompleted &&
    (isLegalResident ? 'ЄДРПОУ' : isSoleTrader ? 'РНОКПП' : '');

  useEffect(() => {
    const identificationNumberParam = searchParams.get('identificationNumber');
    const applicationNumber = searchParams.get('applicationNumber');
    if (!identificationNumberParam || !applicationNumber) {
      removeParam('formId');
      removeParam('identificationNumber');
      removeParam('applicationNumber');
    }
  }, []);

  useEffect(() => {
    const isReidentificationMode = type === IdentificationType.RE_IDENTIFICATION;
    if (isReidentification !== isReidentificationMode) {
      dispatch(setIsReidentification(isReidentificationMode));
    }
    if (!type) {
      setSearchParams('type', IdentificationType.PRIMARY);
    }
  }, [type]);

  useEffect(() => {
    const step = searchParams.get('formId');
    if (step && applicationId && steps.length > 5) {
      const selectedStep =
        steps.find((item) => IdentificationFormSteps[item.name] === +step) ||
        (steps[0] as TIdentificationStep);

      if (selectedStep?.stepStatus === StepStatus.disabled) {
        const availableSteps = steps.filter((item) => item.stepStatus !== StepStatus.disabled);

        const lastStep = availableSteps[availableSteps.length - 1] as TIdentificationStep;
        setActiveStep(lastStep);
        setSearchParams('formId', IdentificationFormSteps[lastStep.name] as unknown as string);
      } else {
        setActiveStep(selectedStep);
      }
      setIsFormDirty(false);
    }
  }, [applicationId, steps.length]);

  const handleNextStep = useCallback(() => {
    dispatch(
      updateFormData({
        field: 'completedForms',
        data: {
          [getCompleatedFormByStepName(activeStep.name)]: true,
        },
      })
    );

    const stepIndex = steps.findIndex((step) => step?.name === activeStep.name);
    const completedFormsInStore = store.getState().identification.formData.completedForms;
    let newActiveStep;

    if (isStepDisabledByName(steps[stepIndex + 1]?.name)) {
      newActiveStep = steps.find(
        (item, index) => !isStepDisabledByName(item.name) && index > stepIndex
      );
    } else {
      newActiveStep = steps[stepIndex + 1] || {};
    }

    setActiveStep(newActiveStep as TIdentificationStep);
    setSearchParams(
      'formId',
      IdentificationFormSteps[
        (newActiveStep?.name || '') as keyof typeof IdentificationFormSteps
      ]?.toString()
    );
    const isCompleted = (name: string) =>
      !!completedFormsInStore[
        getCompleatedFormByStepName(name) as keyof typeof completedFormsInStore
      ];

    setSteps((prevSteps: TIdentificationStep[]) =>
      prevSteps.map((step, index, steps) => {
        if (step.name === activeStep.name) {
          return { ...step, stepStatus: StepStatus.completed };
        } else if (isStepDisabledByName(step.name)) {
          return { ...step, stepStatus: StepStatus.disabled };
        } else if (step.stepStatus === StepStatus.completed) {
          return step;
        } else if (step.name === newActiveStep?.name) {
          return {
            ...step,
            stepStatus: StepStatus.passive,
          };
        } else {
          let prevIndex = index - 1;
          while (prevIndex >= 0 && isStepDisabledByName(steps[prevIndex]?.name)) {
            prevIndex -= 1;
          }
          const isUnlocked = prevIndex <= 0 || isCompleted(steps[prevIndex]?.name as string);

          if (isCompleted(step.name)) return { ...step, stepStatus: StepStatus.completed };
          return { ...step, stepStatus: isUnlocked ? StepStatus.passive : StepStatus.disabled };
        }
      })
    );
    setIsFormDirty(false);
  }, [activeStep, steps, isStepDisabledByName]);

  const components = useGetStepsComponents(handleNextStep);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [activeStep]);

  const handleSetActiveStep = useCallback((value: TIdentificationStep) => {
    setActiveStep(value);
    setSearchParams('formId', IdentificationFormSteps[value.name].toString());
    setIsFormDirty(false);
  }, []);

  const handleStepClick = useCallback(
    (value: TIdentificationStep | (() => void)) => {
      if (isFormDirty) {
        pendingStepRef.current = value;
        setInfoModal((prev) => {
          if (prev.isOpened) return prev;

          const goToPendingStep = () => {
            const step = pendingStepRef.current;

            pendingStepRef.current = null;
            if (typeof step === 'function') step();
            else if (step) {
              if (isStepDisabledByName(step?.name)) {
                const targetIndex = steps.findIndex((item) => item.name === step.name);
                const nextStep = getNextStep(steps, targetIndex, isStepDisabledByName);
                handleSetActiveStep(nextStep);
              } else {
                handleSetActiveStep(step);
              }
            }
          };
          return {
            ...prev,
            isOpened: true,
            onPrimaryButtonClick: () => prev.onPrimaryButtonClick?.(goToPendingStep),
            onSecondaryButtonClick: () => {
              prev.onSecondaryButtonClick?.();
              goToPendingStep();
            },
          };
        });
      } else {
        if (typeof value === 'function') value();
        else handleSetActiveStep(value);
      }
    },
    [handleSetActiveStep, isFormDirty, steps, isStepDisabledByName]
  );

  useEffect(() => {
    if (!isFormDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isFormDirty]);

  return (
    <>
      <Box sx={(theme) => styles.titleBar(theme, isMobile, isTablet, isOpened)}>
        <Box component={'p'}>{identificationText}</Box>
      </Box>
      <IdnetificationFormContext.Provider
        value={{
          infoModal,
          setInfoModal,
          defaultModalInfo,
          isFormDirty,
          setIsFormDirty,
          targetForm,
          setTargetForm,
        }}
      >
        <Box sx={styles.root}>
          <Sidebar
            steps={steps}
            activeStep={activeStep}
            isOpened={isOpened}
            setIsOpened={setIsOpened}
            width={sidebarWidth}
            onStepClick={handleStepClick}
          />
          {isOpened && <Box sx={styles.overlay} />}
          <Box
            sx={[
              styles.container(mainContainerMargin) as SystemStyleObject<Theme>,
              { marginLeft: mainContainerMargin / 8 },
            ]}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
              <Typography variant={isMobile ? 'h6' : 'h4'}>{activeStep?.pageTitle}</Typography>
              {applicationLabel && (
                <Typography variant={isMobile ? 'h6' : 'h4'}>
                  {applicationLabel + ' ' + identificationNumber}
                </Typography>
              )}
            </Box>
            <Paper sx={styles.paper}>
              {components[activeStep?.name as keyof typeof components]}
            </Paper>
          </Box>
        </Box>
      </IdnetificationFormContext.Provider>
      <InfoModal
        isOpened={infoModal.isOpened}
        isDisabledOutsideClickClose={infoModal.isDisabledOutsideClickClose}
        message={infoModal.message}
        onClose={infoModal.onClose}
        title={infoModal.title}
        primaryButtonTitle={infoModal.primaryButtonTitle}
        secondaryButtonTitle={infoModal.secondaryButtonTitle}
        onPrimaryButtonClick={infoModal.onPrimaryButtonClick}
        onSecondaryButtonClick={infoModal.onSecondaryButtonClick}
      />
      <ModalAlert isOpen={!!errorMessage} text={errorMessage} onClose={handleOnErrorAlertClose} />
    </>
  );
};

export default MainForm;
